import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import { useCart } from "../../../contexts/CartContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useNotification } from "../../../contexts/NotificationContext";
import { Heart, ShoppingCart, ChevronLeft } from "lucide-react";

const gradientClass =
  "bg-gradient-to-b from-[#484139] via-[#544C44] via-[#5D554C] via-[#655E54] to-[#6B5B4F]";

const Product = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRating, setSelectedRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewText, setReviewText] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productRef = doc(db, "products", id);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          setProduct({ id: productSnap.id, ...productSnap.data() });
        } else {
          navigate("/products");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        navigate("/products");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
  }, [id, navigate]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!id) return;
      try {
        const reviewsRef = collection(db, "reviews");
        const q = query(reviewsRef, where("productId", "==", id));
        const querySnapshot = await getDocs(q);
        const reviewsData = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((r) =>
            r.comment && r.comment.trim().length > 0 ? r.approved === true : true
          )
          .sort((a, b) => {
            const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt || 0;
            const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt || 0;
            return (
              (bDate instanceof Date ? bDate.getTime() : bDate) -
              (aDate instanceof Date ? aDate.getTime() : aDate)
            );
          });
        setReviews(reviewsData);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };
    fetchReviews();
  }, [id]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      showError("Please log in to submit a review.");
      return;
    }
    if (selectedRating === 0 && !reviewText.trim()) {
      showError("Please add a star rating or write a review.");
      return;
    }

    setSubmittingReview(true);
    try {
      const hasMessage = !!reviewText.trim();
      const reviewData = {
        productId: id,
        userId: currentUser.uid,
        userName:
          currentUser.displayName || currentUser.email?.split("@")[0] || "Anonymous",
        userEmail: currentUser.email,
        rating: selectedRating || 0,
        comment: hasMessage ? reviewText.trim() : "",
        createdAt: serverTimestamp(),
        approved: hasMessage ? false : true,
      };

      await addDoc(collection(db, "reviews"), reviewData);
      showSuccess(
        hasMessage
          ? "Review submitted! It will be visible after admin approval."
          : "Rating submitted successfully!"
      );

      setSelectedRating(0);
      setReviewText("");

      const reviewsRef = collection(db, "reviews");
      const q = query(reviewsRef, where("productId", "==", id));
      const querySnapshot = await getDocs(q);
      const refreshed = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((r) =>
          r.comment && r.comment.trim().length > 0 ? r.approved === true : true
        )
        .sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt || 0;
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt || 0;
          return (
            (bDate instanceof Date ? bDate.getTime() : bDate) -
            (aDate instanceof Date ? aDate.getTime() : aDate)
          );
        });
      setReviews(refreshed);
    } catch (error) {
      console.error("Error submitting review:", error);
      showError("Failed to submit review. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#484139] text-center text-[#D4B998] min-h-screen flex items-center justify-center">
        Loading product...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-[#484139] text-center text-[#D4B998] min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-serif font-bold mb-4">Product not found</h1>
        <button
          onClick={() => navigate("/products")}
          className="bg-[#D4B998] text-[#3b332b] px-6 py-3 rounded-lg font-semibold"
        >
          Back to Products
        </button>
      </div>
    );
  }

  const handleAddToCart = () => {
    try {
      addToCart(product, 1);
      showSuccess(`${product.name} added to cart successfully!`);
    } catch (error) {
      console.error("Error adding to cart:", error);
      showError("Failed to add product to cart");
    }
  };

  const images = Array.isArray(product.images) ? product.images : [];
  const getSrc = (img) =>
    typeof img === "string" ? img : img?.url || "/placeholder-product.jpg";
  const mainImage = images[activeImg] ?? images[0];

  return (
    <div className={`${gradientClass} min-h-screen text-[#D4B998] py-6 sm:py-10`}>
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Back link */}
        <button
          onClick={() => navigate("/products")}
          className="inline-flex items-center gap-1 text-[#D4B998] hover:text-[#e1caa5] mb-5 transition"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">Back to Products</span>
        </button>

        {/* Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square w-full overflow-hidden rounded-2xl bg-white/10">
              <img
                src={mainImage ? getSrc(mainImage) : "/placeholder-product.jpg"}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                {images.slice(0, 4).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    className={`aspect-square overflow-hidden rounded-lg border-2 transition ${
                      activeImg === idx
                        ? "border-[#D4B998]"
                        : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={getSrc(img)}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-[#D4B998] text-[#463C30] rounded-2xl p-5 sm:p-7 lg:p-8 shadow-xl flex flex-col">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-domine font-bold mb-3 sm:mb-4 leading-tight">
              {product.name}
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-[#463C30] mb-4 leading-relaxed">
              {product.description || "No description available."}
            </p>

            <div className="text-2xl sm:text-3xl font-bold mb-4">
              LKR {product.price?.toFixed(2)}
            </div>

            <div className="flex items-center gap-1 mb-6">
              {(() => {
                const displayRating = Math.round(
                  Number(product?.rating ?? product?.avgRating ?? 0)
                );
                return [...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                    fill={i < displayRating ? "#facc15" : "#ffffff"}
                    stroke="#000000"
                    strokeWidth="1"
                  >
                    <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z" />
                  </svg>
                ));
              })()}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <button
                onClick={() =>
                  navigate("/confirm-order", { state: { product, quantity: 1 } })
                }
                className="flex-1 bg-[#463C30] text-[#D4B998] px-6 py-3 rounded-xl font-semibold text-base sm:text-lg hover:bg-[#2e2821] transition"
              >
                Buy Now
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 sm:flex-initial w-full sm:w-12 h-12 flex items-center justify-center gap-2 bg-[#463C30] text-[#D4B998] rounded-xl hover:bg-[#2e2821] transition"
                  aria-label="Add to cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span className="sm:hidden text-sm font-semibold">Add to Cart</span>
                </button>
                <button
                  className="flex-1 sm:flex-initial w-full sm:w-12 h-12 flex items-center justify-center gap-2 bg-[#463C30] text-[#D4B998] rounded-xl hover:bg-[#2e2821] transition"
                  aria-label="Add to wishlist"
                >
                  <Heart className="w-5 h-5" />
                  <span className="sm:hidden text-sm font-semibold">Wishlist</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-10 sm:mt-14 rounded-2xl text-[#E3D5C5]">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-4">Reviews</h2>

          {reviews.length > 0 ? (
            <div className="space-y-3 sm:space-y-4 mb-6">
              {reviews.map((review) => (
                <div key={review.id} className="bg-[#E3D5C5]/20 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#E3D5C5] text-sm sm:text-base">
                        {review.userName}
                      </span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${
                              i < review.rating ? "text-yellow-400" : "text-gray-400"
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <span className="text-xs sm:text-sm text-[#E3D5C5] opacity-70 whitespace-nowrap">
                      {review.createdAt?.toDate
                        ? review.createdAt.toDate().toLocaleDateString()
                        : "Recently"}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base text-[#E3D5C5]">{review.comment}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="mb-6">No reviews yet</p>
          )}

          <h3 className="text-xl sm:text-2xl font-serif font-bold mb-4">Write a review</h3>

          {!currentUser && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4 text-sm">
              Please log in to write a review.
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedRating(i + 1)}
                aria-label={`Rate ${i + 1} star${i === 0 ? "" : "s"}`}
                className="focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-7 h-7"
                  fill={i < selectedRating ? "#facc15" : "#ffffff"}
                  stroke="#000000"
                  strokeWidth="1"
                >
                  <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z" />
                </svg>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmitReview}>
            <textarea
              placeholder="Write a review"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              className="w-full h-28 sm:h-32 p-3 sm:p-4 bg-[#E3D5C5] text-[#83715E] rounded-xl border border-[#bfa57f] focus:outline-none focus:border-[#3b332b] mb-4 resize-none"
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={submittingReview || !currentUser}
                className="flex-1 bg-[#D4B998] text-[#3b332b] py-3 rounded-full font-semibold hover:bg-[#e1caa5] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submittingReview ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#3b332b] border-t-transparent rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  "Submit Review"
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 border-2 border-[#D4B998] text-[#E3D5C5] bg-transparent py-3 rounded-full font-semibold hover:bg-[#e1caa5] hover:text-[#3b332b] transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Product;
